<template>
  <div>
    <!-- KPI stat rows -->
    <div v-for="(row, rIdx) in statsRows" :key="rIdx" class="cards">
      <Card class="stat-card" v-for="stat in row" :key="stat.label">
        <div class="stat-left">
          <div class="stat-label">{{ stat.label }}</div>
          <div class="stat-value number--text">
            <!-- Animated display value -->
            <span :class="{ 'na-text': _isNA(stat) }">
              {{ stat.animated !== undefined ? stat.animated : (stat.value === null ? 'N/A' : stat.value) }}
            </span>
            <span class="stat-unit" v-if="!_isNA(stat)">{{ stat.unit }}</span>
          </div>
        </div>
        <div class="icon-wrap" :style="{ background: stat.iconBg }">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" v-html="stat.icon"></svg>
        </div>
      </Card>
    </div>

    <!-- Inverter table -->
    <div class="table-header-row">
      <span class="table-title">Inverters</span>
      <!-- Search box -->
      <div class="search-wrap">
        <v-icon small class="search-icon">search</v-icon>
        <input
          v-model="inverterSearch"
          class="search-input"
          placeholder="Search inverter…"
          type="text"
        />
        <v-icon v-if="inverterSearch" small class="clear-icon" @click="inverterSearch = ''">close</v-icon>
      </div>
    </div>

    <div class="table-wrap">
      <table class="inv-table">
        <thead>
          <tr>
            <th>Inverter Name</th>
            <th>DC Cap <span class="unit">(kWp)</span></th>
            <th>Live <span class="unit">(kW)</span></th>
            <th>Set <span class="unit">(kW)</span></th>
            <th>Utilization</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!filteredInverters.length">
            <td colspan="5" class="empty-row">No inverters match "{{ inverterSearch }}"</td>
          </tr>
          <tr
            v-for="(row, idx) in filteredInverters"
            :key="idx"
            :class="{ 'row-inactive': !isInverterActive(row.deviceId) }"
          >
            <td>
              <div class="inv-name">
                <span class="status-dot" :class="isInverterActive(row.deviceId) ? 'dot-green' : 'dot-red'"></span>
                <span class="inv-name-text">{{ row.name }}</span>
              </div>
            </td>
            <td><span class="number--text">{{ row.dcCapacity }}</span></td>
            <td>
              <span class="live-chip" :class="isInverterActive(row.deviceId) ? 'on' : 'off'">
                {{ displayInverterValue(row.deviceId, 'liveValue') }}
              </span>
            </td>
            <td>
              <span class="live-chip" :class="(getInverterValue(row.deviceId, 'setValue') || 0) > 0 ? 'on' : 'off'">
                {{ displayInverterValue(row.deviceId, 'setValue') }}
              </span>
            </td>
            <td>
              <!-- Utilization bar: liveValue / dcCapacity -->
              <div class="util-wrap">
                <div class="util-bar-bg">
                  <div
                    class="util-bar-fill"
                    :style="{ width: utilizationPct(row.deviceId, row.dcCapacity) + '%' }"
                    :class="utilizationClass(row.deviceId, row.dcCapacity)"
                  ></div>
                </div>
                <span class="util-label number--text">{{ utilizationLabel(row.deviceId, row.dcCapacity) }}</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
const ICONS = {
  activeDg: `<circle cx="12" cy="12" r="4" fill="#4CAF50"/>
             <circle cx="12" cy="12" r="8" stroke="#4CAF50" stroke-width="1.5" stroke-dasharray="3 2" fill="none" opacity="0.5"/>
             <circle cx="12" cy="12" r="11" stroke="#4CAF50" stroke-width="1" fill="none" opacity="0.25"/>`,
  runtime: `<circle cx="12" cy="12" r="9" stroke="#64B5F6" stroke-width="1.5" fill="none"/>
            <line x1="12" y1="12" x2="12" y2="7" stroke="#64B5F6" stroke-width="1.8" stroke-linecap="round"/>
            <line x1="12" y1="12" x2="16" y2="14" stroke="#64B5F6" stroke-width="1.5" stroke-linecap="round"/>
            <circle cx="12" cy="12" r="1.2" fill="#64B5F6"/>`,
  dgEnergyToday: `<polygon points="13,3 4,14 12,14 11,21 20,10 12,10" fill="#FFB74D" opacity="0.9"/>`,
  dcCapacity: `<rect x="3" y="8" width="18" height="11" rx="2" stroke="#9575CD" stroke-width="1.5" fill="none"/>
               <rect x="7" y="5" width="10" height="4" rx="1" stroke="#9575CD" stroke-width="1.2" fill="none"/>
               <line x1="8" y1="13" x2="8" y2="16" stroke="#9575CD" stroke-width="1.5" stroke-linecap="round"/>
               <line x1="12" y1="13" x2="12" y2="16" stroke="#9575CD" stroke-width="1.5" stroke-linecap="round"/>
               <line x1="16" y1="13" x2="16" y2="16" stroke="#9575CD" stroke-width="1.5" stroke-linecap="round"/>`,
  activeInverters: `<rect x="3" y="4" width="18" height="5" rx="1.5" stroke="#26C6DA" stroke-width="1.4" fill="none"/>
                   <rect x="3" y="10" width="18" height="5" rx="1.5" stroke="#26C6DA" stroke-width="1.4" fill="none"/>
                   <rect x="3" y="16" width="18" height="5" rx="1.5" stroke="#26C6DA" stroke-width="1.4" fill="none"/>
                   <circle cx="18.5" cy="6.5" r="1" fill="#26C6DA"/>
                   <circle cx="18.5" cy="12.5" r="1" fill="#26C6DA"/>
                   <circle cx="18.5" cy="18.5" r="1" fill="#26C6DA"/>
                   <line x1="6" y1="6.5" x2="14" y2="6.5" stroke="#26C6DA" stroke-width="1" stroke-linecap="round" opacity="0.5"/>
                   <line x1="6" y1="12.5" x2="14" y2="12.5" stroke="#26C6DA" stroke-width="1" stroke-linecap="round" opacity="0.5"/>
                   <line x1="6" y1="18.5" x2="14" y2="18.5" stroke="#26C6DA" stroke-width="1" stroke-linecap="round" opacity="0.5"/>`,
  energyTodaySolar: `<circle cx="12" cy="12" r="4" fill="#FFD54F" opacity="0.9"/>
                     <line x1="12" y1="3" x2="12" y2="5.5" stroke="#FFD54F" stroke-width="1.8" stroke-linecap="round"/>
                     <line x1="12" y1="18.5" x2="12" y2="21" stroke="#FFD54F" stroke-width="1.8" stroke-linecap="round"/>
                     <line x1="3" y1="12" x2="5.5" y2="12" stroke="#FFD54F" stroke-width="1.8" stroke-linecap="round"/>
                     <line x1="18.5" y1="12" x2="21" y2="12" stroke="#FFD54F" stroke-width="1.8" stroke-linecap="round"/>
                     <line x1="5.6" y1="5.6" x2="7.4" y2="7.4" stroke="#FFD54F" stroke-width="1.5" stroke-linecap="round"/>
                     <line x1="16.6" y1="16.6" x2="18.4" y2="18.4" stroke="#FFD54F" stroke-width="1.5" stroke-linecap="round"/>
                     <line x1="18.4" y1="5.6" x2="16.6" y2="7.4" stroke="#FFD54F" stroke-width="1.5" stroke-linecap="round"/>
                     <line x1="7.4" y1="16.6" x2="5.6" y2="18.4" stroke="#FFD54F" stroke-width="1.5" stroke-linecap="round"/>`
};

// Simple integer count-up tween
function animateValue(from, to, durationMs, onUpdate) {
  const start = performance.now();
  function step(now) {
    const progress = Math.min((now - start) / durationMs, 1);
    // ease-out cubic
    const eased = 1 - Math.pow(1 - progress, 3);
    onUpdate(from + (to - from) * eased);
    if (progress < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

export default {
  name: 'KPICards',

  props: {
    kpiData: {
      type: Object,
      default: () => ({
        activeDg: '0',
        runtime: 'N/A',
        dgEnergyToday: 'N/A',
        dcCapacity: 'N/A',
        activeInverters: 'N/A',
        energyTodaySolar: 'N/A'
      })
    },
    invertersConfig: {
      type: Array,
      default: () => []
    },
    inverterData: {
      type: Object,
      default: () => ({})
    },
    mode: {
      type: String,
      default: 'zes'
    }
  },

  data() {
    return {
      inverterSearch: '',
      // Animated display values for numeric KPI cards
      animatedValues: {
        dgEnergyToday: null,
        dcCapacity: null,
        energyTodaySolar: null,
        runtime: null,
      },
    };
  },

  mounted() {
    this._runCountUp();
  },

  computed: {
    stats() {
      return [
        { key: 'activeDg', label: 'Active / Total DG', value: this.kpiData.activeDg, unit: '', iconBg: 'rgba(76,175,80,0.15)', icon: ICONS.activeDg },
        { key: 'runtime', label: 'DG Runtime', value: this.kpiData.runtime, unit: 'mins', iconBg: 'rgba(100,181,246,0.15)', icon: ICONS.runtime, animated: this._fmt(this.animatedValues.runtime) },
        { key: 'dgEnergyToday', label: 'DG Energy Today', value: this.kpiData.dgEnergyToday, unit: 'kWh', iconBg: 'rgba(255,183,77,0.15)', icon: ICONS.dgEnergyToday, animated: this._fmt(this.animatedValues.dgEnergyToday) },
        { key: 'activeInverters', label: 'Active / Total Inverters', value: this.kpiData.activeInverters, unit: '', iconBg: 'rgba(38,198,218,0.15)', icon: ICONS.activeInverters },
        { key: 'dcCapacity', label: 'Total DC Capacity', value: this.kpiData.dcCapacity, unit: 'kWp', iconBg: 'rgba(149,117,205,0.15)', icon: ICONS.dcCapacity, animated: this._fmt(this.animatedValues.dcCapacity) },
        { key: 'energyTodaySolar', label: 'Total Solar Energy', value: this.kpiData.energyTodaySolar, unit: 'kWh', iconBg: 'rgba(255,213,79,0.15)', icon: ICONS.energyTodaySolar, animated: this._fmt(this.animatedValues.energyTodaySolar) },
      ];
    },

    statsRows() {
      if (this.mode === 'zes') return [this.stats.slice(3)];
      return [this.stats.slice(0, 3), this.stats.slice(3)];
    },

    filteredInverters() {
      const q = this.inverterSearch.toLowerCase().trim();
      let list = q
        ? this.invertersConfig.filter(inv => inv.name.toLowerCase().includes(q))
        : [...this.invertersConfig];

      return list;
    },
  },

  methods: {
    _getInv(deviceId) {
      return this.inverterData[`inverter-${deviceId}`] != null ? this.inverterData[`inverter-${deviceId}`] : null;
    },

    getInverterValue(deviceId, field) {
      const inv = this._getInv(deviceId);
      if (!inv) return null;
      const val = inv[field];
      return val !== undefined ? val : null;
    },

    isInverterActive(deviceId) {
      const inv = this._getInv(deviceId);
      return inv ? inv.liveValue > 0 : false;
    },

    _isNA(stat) {
      const display = stat.animated !== undefined ? stat.animated : stat.value;
      return display === null || display === 'N/A';
    },

    utilizationPct(deviceId, dcCapacity) {
      const live = this.getInverterValue(deviceId, 'liveValue');
      const cap = parseFloat(dcCapacity) || 0;
      if (!cap) return 0;
      return Math.min(Math.round((live / cap) * 100), 100);
    },

    utilizationClass(deviceId, dcCapacity) {
      const pct = this.utilizationPct(deviceId, dcCapacity);
      if (pct >= 75) return 'util-high';
      if (pct >= 35) return 'util-mid';
      return 'util-low';
    },

    displayInverterValue(deviceId, field) {
      const val = this.getInverterValue(deviceId, field);
      return val === null ? 'N/A' : val;
    },

    utilizationLabel(deviceId, dcCapacity) {
      const inv = this._getInv(deviceId);
      if (!inv) return 'N/A';
      return this.utilizationPct(deviceId, dcCapacity) + '%';
    },

    _fmt(val) {
      if (val === null || val === undefined) return 'N/A';
      const n = parseFloat(val);
      if (isNaN(n)) return 'N/A';
      return Number.isInteger(n) ? n.toString() : n.toFixed(2);
    },

    _runCountUp() {
      const targets = ['dgEnergyToday', 'dcCapacity', 'energyTodaySolar', 'runtime'];
      targets.forEach(key => {
        const target = parseFloat(this.kpiData[key]);
        if (isNaN(target)) {          // keeps animatedValues[key] = null → shows N/A
          this.animatedValues[key] = null;
          return;
        }
        animateValue(0, target, 1200, v => { this.animatedValues[key] = v; });
      });
    },
  },

  watch: {
    // Re-run count-up when numeric KPI values change (e.g. after date filter)
    'kpiData.energyTodaySolar'(newVal, oldVal) {
      const from = parseFloat(oldVal) || 0;
      const to   = parseFloat(newVal);
      if (isNaN(to)) { this.animatedValues.energyTodaySolar = null; return; }
      animateValue(from, to, 800, v => { this.animatedValues.energyTodaySolar = v; });
    },
    'kpiData.dgEnergyToday'(newVal, oldVal) {
      const from = parseFloat(oldVal) || 0;
      const to = parseFloat(newVal);
      if (isNaN(to)) { this.animatedValues.dgEnergyToday = null; return; }
      animateValue(from, to, 800, (v) => { this.animatedValues.dgEnergyToday = v; });
    },
    'kpiData.dcCapacity'(newVal, oldVal) {
      const from = parseFloat(oldVal) || 0;
      const to   = parseFloat(newVal);
      if (isNaN(to)) { this.animatedValues.dcCapacity = null; return; }
      animateValue(from, to, 800, (v) => { this.animatedValues.dcCapacity = v; });
    },
    'kpiData.runtime'(newVal, oldVal) {
      const from = parseFloat(oldVal) || 0;
      const to   = parseFloat(newVal);
      if (isNaN(to)) { this.animatedValues.runtime = null; return; }
      animateValue(from, to, 800, (v) => { this.animatedValues.runtime = v; });
    },
  }
};
</script>

<style scoped>
.cards {
  display: flex;
  gap: 8px;
}

.stat-card {
  flex: 1;
  padding: 16px !important;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-shadow: 0px 5px 5px 0px #334079;
  border-radius: 8px;
  margin-bottom: 6px;
}

.stat-left {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--v-primaryText-base);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  line-height: 1.3;
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.1;
}

.stat-unit {
  font-size: 13px;
  font-weight: 400;
}

/* ── Table header row ─────────────────────────────── */
.table-header-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  margin-top: 2px;
}

.table-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--v-primaryText-base);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex: 1;
}

/* ── Search box ───────────────────────────────────── */
.search-wrap {
  display: flex;
  align-items: center;
  background: rgba(51, 64, 121, 0.3);
  border: 1px solid #334079;
  border-radius: 8px;
  padding: 3px 8px;
  gap: 4px;
}

.search-icon { color: #668CF6; opacity: 0.7; }

.search-input {
  background: transparent;
  border: none;
  outline: none;
  color: var(--v-primaryText-base);
  font-size: 12px;
  width: 120px;
}

.search-input::placeholder { color: #667; }

.clear-icon {
  cursor: pointer;
  color: #8899cc;
  opacity: 0.7;
  transition: opacity 0.2s;
}
.clear-icon:hover { opacity: 1; }

/* ── Inverter table ───────────────────────────────── */
.inv-table {
  width: 100%;
  font-size: 13px;
  box-shadow: 0px 5px 5px 0px #334079;
  border-radius: 8px;
  background: var(--v-primary-base);
}

.inv-table th {
  padding: 10px 14px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: var(--v-primaryText-base);
  letter-spacing: 0.6px;
  border-bottom: 1px solid #334079;
}

.inv-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--v-primary-base);
}

.inv-table th .unit {
  font-weight: 400;
  font-size: 11px;
  color: var(--v-primaryText-base);
}

.inv-table td {
  padding: 10px 10px;
  border-bottom: 1px solid rgba(51, 64, 121, 0.35);
}

/* Dim inactive rows slightly */
.row-inactive td { opacity: 0.55; }

.status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.dot-green { background: #4CAF50; box-shadow: 0 0 5px rgba(76,175,80,0.7); }
.dot-red   { background: #FF5252; box-shadow: 0 0 5px rgba(255,82,82,0.7); }

.table-wrap {
  width: 100%;
  margin-top: 0;
  border-radius: 10px;
  border: 1px solid #1e2f5e;
  max-height: calc(100vh - 50vh);
  overflow-y: auto;
  overflow-x: hidden;
}

.table-wrap::-webkit-scrollbar { width: 6px; }
.table-wrap::-webkit-scrollbar-track { background: transparent; }
.table-wrap::-webkit-scrollbar-thumb { background: #334079; border-radius: 4px; }
.table-wrap::-webkit-scrollbar-thumb:hover { background: #668CF6; }

.inv-name { display: flex; align-items: center; gap: 8px; }
.inv-name-text { font-size: 13px; color: var(--v-primaryText-base); font-weight: 500; }

.live-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.live-chip.on  { color: #66BB6A; }
.live-chip.off { color: #EF5350; }

/* ── Utilization bar ───────────────────────────────── */
.util-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}

.util-bar-bg {
  flex: 1;
  height: 6px;
  background: rgba(51, 64, 121, 0.5);
  border-radius: 4px;
  overflow: hidden;
}

.util-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.6s ease;
}

.util-high  { background: #4CAF50; }
.util-mid   { background: #FFB74D; }
.util-low   { background: #EF5350; }

.util-label {
  font-size: 11px;
  font-weight: 600;
  min-width: 32px;
  text-align: right;
}

.empty-row {
  text-align: center;
  padding: 20px;
  color: #8899cc;
  font-size: 13px;
}

.na-text {
  color: #EF5350;
  font-weight: 600;
}

th:first-child, td:first-child { text-align: left; }
th:not(:first-child), td:not(:first-child) { text-align: center; }
</style>
